import { BasicTabProps } from "@components/common/tabs/tabs-type";
import { accountSettingsPreferencesTab } from "@pages/account-settings/account-settings-preferences-tab";
import { accountSettingsSecurityTab } from "@pages/account-settings/account-settings-security-tab";
import { integrationsAccountTab } from "@pages/account-settings/integrations-account/integrations-account-tab";
import { useUserContext } from "@pages/account-settings/user-provider";
import { AccountSettingsTabs, QueryParams } from "@router/route-params";
import { generateAccountSettingsRoute } from "@router/router-helper";
import { useRedirectToPage } from "@hooks/use-redirect-to-page";
import { useMemo } from "react";
import { accountSettingsPrivacyTab } from "@pages/account-settings/integrations-account/privacy-account-tab";
import { SettingsApiTypes } from "@stellar/settings-api-client";

interface UseAccountSettingsProps {
  /** The tabs used for account settings */
  accountSettingsTabs: Record<
    AccountSettingsTabs,
    BasicTabProps<AccountSettingsTabs>
  >;
}

/** The tabs used for account settings */
export function useAccountSettingsTabs(): UseAccountSettingsProps {
  const { user } = useUserContext();

  const accountSettingsTabs: Record<
    AccountSettingsTabs,
    BasicTabProps<AccountSettingsTabs>
  > = useMemo(() => {
    return {
      [AccountSettingsTabs.security]: {
        routeId: AccountSettingsTabs.security,
        label: "Account & security",
        isHidden: !user,
        content: user
          ? accountSettingsSecurityTab({
              user,
            })
          : [],
      },

      [AccountSettingsTabs.preferences]: {
        routeId: AccountSettingsTabs.preferences,
        label: "Language & units",
        content: accountSettingsPreferencesTab(),
      },

      [AccountSettingsTabs.privacy]: {
        routeId: AccountSettingsTabs.privacy,
        label: "Privacy",
        content: accountSettingsPrivacyTab(),
      },

      [AccountSettingsTabs.integrations]: {
        routeId: AccountSettingsTabs.integrations,
        label: "Integration accounts",
        isHidden: !user,
        content: user ? integrationsAccountTab() : [],
      },
    };
  }, [user]);

  return {
    accountSettingsTabs,
  };
}

/**
 *
 * Hook to redirect to the account settings page; preserves the origin route in the query params.
 * It will redirect outside the SPA and pass the current pathname and query params
 */
export function useRedirectToAccountSettingsPage() {
  const redirectToPage = useRedirectToPage();
  return (accountSettingsTab: AccountSettingsTabs) => {
    const route = generateAccountSettingsRoute(accountSettingsTab);
    redirectToPage({
      route,
      options: {
        shouldPreserveOrigin: true,
        originParamName: QueryParams.settingsOriginUrl,
      },
    });
  };
}

/** Return true when the guest user is allowed to open the account settings tab */
export function canGuestOpenAccountSettingsTab(
  accountSettingsTab?: string
): boolean {
  return (
    accountSettingsTab === AccountSettingsTabs.preferences ||
    accountSettingsTab === AccountSettingsTabs.privacy
  );
}

/** Returns the header ("Metric" or "Imperial") for the given unit type */
export function getUnitHeader(
  unitType: SettingsApiTypes.UnitMeasurement
): "Metric" | "Imperial" {
  return unitType === "meters" || unitType === "millimeters"
    ? "Metric"
    : "Imperial";
}
